import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { routes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { BcIconModule } from '@bancolombia/design-system-web/bc-icon';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';

import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';

import {
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalService,
  MsalGuard,
} from '@azure/msal-angular';
import { loadingInterceptor } from './core/interceptor/loading.interceptor';
import { BcStrokeModule } from '@bancolombia/design-system-web/bc-stroke';
import { environment } from '@env';

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log('MSAL: ',logLevel, message);
}

export function mSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: environment.msalConfig.auth.redirectUri,
      postLogoutRedirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function mSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    environment.apiConfig.uri,
    environment.apiConfig.scopes
  );

  protectedResourceMap.set(
    `${environment.api}/*`,
    [environment.apiScope]
  );

  protectedResourceMap.set(
    `${environment.domain}/${environment.maintenancePath}*`,
    [environment.apiScope]
  );

  protectedResourceMap.set(
    `${environment.domain}/${environment.paramsisPath}*`,
    [environment.apiScope]
  );

  protectedResourceMap.set(
    `${environment.domain}/${environment.historyPath}*`,
    [environment.apiScope]
  );

  protectedResourceMap.set(
    `${environment.domain}/${environment.userManagementPath}*`,
    [environment.apiScope]
  );

  protectedResourceMap.set(
    `${environment.domain}/${environment.homonymsPath}*`,
    [environment.apiScope]
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function mSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/login-failed',
  };
}


export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideAnimations(),
    importProvidersFrom(
      BcIconModule.forRoot({
        path: environment.pathIcon,
      }),
      BcStrokeModule.forRoot({
        path: environment.pathIcon,
      })
    ),
    provideHttpClient(withInterceptors([loadingInterceptor]), withInterceptorsFromDi(), withFetch()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: mSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: mSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: mSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
};
